/** Generic rolling/custom date-range filtering, shared by every view that offers a Today/7d/30d/90d/
 *  All/custom window (Stats, the Timeline and plugin pages). Pure, side-effect-free so the window logic is
 *  unit-testable independent of any view. Presets are *rolling* (computed from "now"); a custom
 *  range pins explicit calendar days, interpreted locally by rangeBounds or in UTC by utcDayWindow. */

import { utcDayOf } from './utcDay';

import type { DateRange, RangePreset, InstantRange, InstantRangeBounds, InstantRangeConstraints } from 'elowen-plugin-ui-kit';
export type { DateRange, RangePreset } from 'elowen-plugin-ui-kit';
export const RANGE_PRESETS: readonly RangePreset[] = ['7d', '30d', '90d', 'today', 'all', 'custom'];

export const DEFAULT_RANGE: DateRange = { preset: '7d', from: null, to: null };

const instantMs = (value: string): number => {
  const ms = Date.parse(value);
  return Number.isFinite(ms) && new Date(ms).toISOString() === value ? ms : NaN;
};

/** Validate fixed custom instants; live windows are resolved by their server. */
export function instantRangeBounds(range: Pick<InstantRange, 'from' | 'to'>, constraints: InstantRangeConstraints = {}): InstantRangeBounds {
  if (!range.from || !range.to) return { valid: false, error: 'incomplete' };
  const fromMs = instantMs(range.from);
  const toMs = instantMs(range.to);
  const min = constraints.min === undefined ? -Infinity : instantMs(constraints.min);
  const max = constraints.max === undefined ? Infinity : instantMs(constraints.max);
  if (!Number.isFinite(new Date(fromMs).getTime()) || !Number.isFinite(new Date(toMs).getTime()) || Number.isNaN(min) || Number.isNaN(max) || min > max) {
    return { valid: false, error: 'invalid' };
  }
  if (fromMs >= toMs) return { valid: false, error: 'order' };
  if (fromMs < min || toMs > max) return { valid: false, error: 'bounds' };
  return { valid: true, from: new Date(fromMs).toISOString(), to: new Date(toMs).toISOString(), fromMs, toMs };
}

/** HTML datetime-local value with millisecond precision, in the browser's timezone. */
export function instantToLocalInput(iso: string): string {
  const date = new Date(iso);
  const pad = (value: number, length = 2) => String(value).padStart(length, '0');
  return `${pad(date.getFullYear(), 4)}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}.${pad(date.getMilliseconds(), 3)}`;
}

/** Date chooses the earlier occurrence of a repeated DST hour. Round-trip validation rejects
 * nonexistent spring-forward times and normalized invalid calendar dates instead of shifting them. */
export function localInputToInstant(value: string): string | null {
  const [day, time, extra] = value.split('T');
  const parts = time?.split(':');
  if (extra !== undefined || !day || !parts || parts.length < 2 || parts.length > 3) return null;
  const [seconds = '00', fraction = ''] = (parts[2] ?? '00').split('.');
  const normalized = `${day}T${parts[0]}:${parts[1]}:${seconds}.${fraction.padEnd(3, '0')}`;
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return null;
  const iso = date.toISOString();
  return instantToLocalInput(iso) === normalized ? iso : null;
}

/** Calendar-day labels for provider counters and token usage, interpreted in UTC. */
export function utcDayLabel(day: string, locale: string, fullYear = false): string {
  return new Date(`${day}T00:00:00.000Z`).toLocaleDateString(locale, {
    ...(fullYear ? { year: 'numeric' as const } : {}), month: 'short', day: 'numeric', timeZone: 'UTC',
  });
}

const DAY_MS = 86_400_000;
const isDateStr = (s: string): boolean => /^\d{4}-\d{2}-\d{2}$/.test(s);

/** Local start-of-day for an epoch ms. */
const startOfDay = (ms: number): number => { const d = new Date(ms); d.setHours(0, 0, 0, 0); return d.getTime(); };
/** Local end-of-day for an epoch ms. */
const endOfDay = (ms: number): number => { const d = new Date(ms); d.setHours(23, 59, 59, 999); return d.getTime(); };
/** Local day bounds from a `YYYY-MM-DD` string (the `T..` form forces local, not UTC, parsing). */
const startOfDayStr = (s: string): number => new Date(`${s}T00:00:00`).getTime();
const endOfDayStr = (s: string): number => new Date(`${s}T23:59:59.999`).getTime();

/** Serialize for a single localStorage slot: `preset|from|to` (empty segments for null). */
export function serializeRange(r: DateRange): string {
  return `${r.preset}|${r.from ?? ''}|${r.to ?? ''}`;
}

/** Parse a stored value back to a range; returns null on anything malformed so the caller keeps the
 *  default rather than restoring junk. */
export function parseRange(raw: string): DateRange | null {
  const parts = raw.split('|');
  // Legacy single-preset form (the old Timeline stored just the preset string, no from/to) — accept it as a
  // preset window so a saved 7d/30d/all range survives the collapse onto this shared model.
  if (parts.length === 1) {
    return raw !== 'custom' && (RANGE_PRESETS as readonly string[]).includes(raw) ? { preset: raw as RangePreset, from: null, to: null } : null;
  }
  if (parts.length !== 3) return null;
  const [preset, from, to] = parts as [string, string, string];
  if (!(RANGE_PRESETS as readonly string[]).includes(preset)) return null;
  if (from && !isDateStr(from)) return null;
  if (to && !isDateStr(to)) return null;
  return { preset: preset as RangePreset, from: from || null, to: to || null };
}

/** Predicate for usePersistentState — true when the raw stored string is a well-formed range. */
export const isStoredRange = (raw: string): boolean => parseRange(raw) !== null;

/** Effective `[fromMs, toMs]` window. Presets reach from N-1 days before today up to now-and-beyond
 *  (so upcoming scheduled tasks stay visible); custom uses inclusive local day bounds, open on any
 *  side left blank. */
export function rangeBounds(r: DateRange, now: number): { fromMs: number; toMs: number } {
  if (r.preset === 'all') return { fromMs: -Infinity, toMs: Infinity };
  if (r.preset === 'today') return { fromMs: startOfDay(now), toMs: endOfDay(now) };
  if (r.preset === 'custom') {
    return {
      fromMs: r.from ? startOfDayStr(r.from) : -Infinity,
      toMs: r.to ? endOfDayStr(r.to) : Infinity,
    };
  }
  const days = r.preset === '7d' ? 7 : r.preset === '30d' ? 30 : 90;
  return { fromMs: startOfDay(now) - (days - 1) * DAY_MS, toMs: Infinity };
}

const utcDayStart = (atMs: number): number => Date.parse(`${utcDayOf(atMs)}T00:00:00.000Z`);
const utcDayMs = (day: string): number => Date.parse(`${day}T00:00:00.000Z`);

/** Inclusive UTC days for counter-backed views, clipped to today. With `maxDays`, "all" and open
 *  custom ranges cover that bounded history. Without it, a null lower bound preserves the entire history.
 *  Local-calendar views keep using {@link rangeBounds}. */
export function utcDayWindow(range: DateRange, now: number, maxDays: number): { from: string; to: string };
export function utcDayWindow(range: DateRange, now: number): { from: string | null; to: string };
export function utcDayWindow(range: DateRange, now: number, maxDays?: number): { from: string | null; to: string } {
  const today = utcDayStart(now);
  const picked = (day: string | null) => (day ? utcDayMs(day) : Number.NaN);
  const to = range.preset === 'custom' && Number.isFinite(picked(range.to)) ? Math.min(picked(range.to), today) : today;
  const earliest = maxDays === undefined ? -Infinity : to - (maxDays - 1) * DAY_MS;
  const presetDays = { today: 1, '7d': 7, '30d': 30, '90d': 90 } as const;
  const requested = range.preset === 'custom' ? picked(range.from)
    : range.preset === 'all' ? earliest
      : to - (presetDays[range.preset] - 1) * DAY_MS;
  const from = Math.min(to, Math.max(Number.isFinite(requested) ? requested : earliest, earliest));
  return { from: Number.isFinite(from) ? utcDayOf(from) : null, to: utcDayOf(to) };
}

/** Every UTC day from `from` to `to`, inclusive, for a chart that draws a day without a row as zero. */
export function utcDaysBetween(from: string, to: string): string[] {
  const days: string[] = [];
  for (let at = utcDayMs(from); at <= utcDayMs(to); at += DAY_MS) days.push(utcDayOf(at));
  return days;
}
